import React, { useEffect, useRef, useState } from 'react';
import Whiteboard from './Whiteboard';
import { randomId } from '@aha-app/mvc';
import Topbar from './components/TopBar';
import styles from './styles.module.css';
import classnames from 'classnames';
import ReactDOM from 'react-dom';
import useResizeObserver from './components/hooks/useResizeObserver';
import type WhiteboardController from './controllers/WhiteboardController';
import { type RecordId } from './types';
import type WidgetsController from './controllers/WidgetsController';

const defaultWhiteboardConfiguration = {
  readOnly: false,
  fayeClient: null,
  connection: null,
  documentCollection: '5810406926123411960--whiteboard',
  documentId: '123--123--123',
  accountId: '5810406926123411960',
  whiteboardId: '7035286571029269812',
  userId: Math.round(Math.random() * 100),
  resolveUserInfo: async (userId: RecordId) => ({
    displayName: `Chris Waters ${userId}`,
    avatar:
      'https://cdn.aha.io/assets/avatar32.7573953ba907c3c29dd540ce4febcba6.svg',
  }),
  resolveRecordInfo: () => ({}),
  createIdeas: () => ({}),
  defaultProject: {
    id: '1234',
    name: 'Really Big Aha!',
  },
  uploadAttachment: async ({ file }) => {
    const formData = new FormData();
    formData.append('file', file);

    const attachmentId = randomId();
    const response = await fetch(`/attachments/${attachmentId}`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error('Failed to upload image');
    }

    return attachmentId;
  },
};

export const NonCollaborativeWhiteboard = ({
  allowFullScreen = false,
  readOnly = true,
  isFormField = false,
  initialVersion = 1,
  initialData = {},
  isEmbeddedPreview = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onFullScreenChange = (_f: boolean) => ({}),
}) => {
  const wbControllerRef = useRef<WhiteboardController & WidgetsController>(
    null
  );
  const [fullScreen, setFullScreen] = useState(false);
  const inDocNode = useRef<HTMLDivElement>(null);
  const [node] = React.useState(() => {
    const elem = document.createElement('div');
    elem.setAttribute('class', 'whiteboard-container-portal');
    return elem;
  });

  useEffect(() => {
    if (fullScreen) {
      document.body.appendChild(node);
    } else {
      inDocNode.current?.appendChild(node);
    }

    return () => {
      node.remove();
    };
  }, [fullScreen, inDocNode, node]);

  useEffect(() => {
    if (fullScreen) {
      wbControllerRef.current?.actionFocus();
    }
    onFullScreenChange(fullScreen);
  }, [fullScreen, onFullScreenChange]);

  const resizeCallback = (element: HTMLElement) => {
    if (!element) return;
    window.dispatchEvent(new Event('resize'));
    wbControllerRef.current?.actionZoomIntoView();
    if (fullScreen) {
      wbControllerRef.current?.actionFocus();
    }
  };

  const resizeRef = useResizeObserver<HTMLDivElement>(resizeCallback);

  let topControls: React.ReactNode | null = null;
  if (allowFullScreen) {
    if (fullScreen) {
      topControls = (
        <Topbar
          onClose={() => {
            setFullScreen(false);
          }}
        />
      );
    } else {
      // Show fullscreen button in bottom right of whiteboard
      topControls = (
        <div className={styles.whiteboardActions}>
          <aha-button
            kind='default'
            onClick={() => setFullScreen(true)}
            aria-label='Enter fullscreen mode'
          >
            <i className='fa-solid fa-up-right-and-down-left-from-center' />
          </aha-button>
        </div>
      );
    }
  }

  return (
    <div ref={inDocNode} style={{ width: '100%', height: '100%' }}>
      {ReactDOM.createPortal(
        <div
          ref={resizeRef}
          className={classnames(styles.nonCollabContainer, {
            [styles.nonCollabContainerFullscreen]: fullScreen,
            'whiteboard-fullscreen': fullScreen,
          })}
        >
          <Whiteboard
            {...defaultWhiteboardConfiguration}
            // @ts-expect-error we're not typing the expected controller state
            controllerRef={wbControllerRef}
            readOnly={readOnly}
            isFormField={isFormField}
            allowAnnotate={isFormField}
            allowUpdate={isFormField}
            initialVersion={initialVersion}
            initialData={initialData}
            isEmbeddedPreview={isEmbeddedPreview && !fullScreen}
          />
          {topControls}
        </div>,
        node
      )}
    </div>
  );
};
